import { Campaign } from '@/types/campaign.types';
import TableData from '@layouts/layout-table/layout-table-data';
import TableRow from '@layouts/layout-table/layout-table-row';
import Text from '@ui/text/text/text';
import Pill from '@ui/pill/pill';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Checkbox from '@/general-ui/checkbox/checkbox';
import { getCampaignStatusColor, getCampaignStatusLabel } from '../utils';
import { useMemo } from 'react';
import { niceDateTimeTz } from '@/utils/dates';
import { EmailTypeDisplayNames, EmailTypeEnum } from '@/types/email.types';

interface CampaignsTableRowProps {
  campaign: Campaign;
  onEdit: (id: string) => void;
  selected: boolean;
  onSelect: (id: string) => void;
}

const CampaignsTableRow = ({ campaign, onEdit, selected, onSelect }: CampaignsTableRowProps) => {
  const { title, state, scheduledAt, sourceScheduledEmail } = campaign;
  const statusColor = getCampaignStatusColor(state);

  const includedAudiences = useMemo(() => {
    if (sourceScheduledEmail?.email_type === EmailTypeEnum.event_registration_email) {
      return { label: 'All', rest: 0 };
    }

    const labeled = sourceScheduledEmail?.audiences.reduce<{ label: string; rest: number }>(
      (list, audience, i) => {
        if (i === 0) {
          list.label = audience.label;
        } else {
          list.rest += 1;
        }
        return list;
      },
      { label: '', rest: 0 }
    );

    if (labeled?.label) {
      return labeled;
    }

    return { label: 'All', rest: 0 };
  }, [sourceScheduledEmail]);

  return (
    <TableRow
      hoverBackgroundColor="var(--global-cont-1)"
      focusWithinBackgroundColor="var(--global-cont-1)"
      border={0}
      className="cursor-pointer"
      onClick={() => onEdit(campaign.uuid)}
      backgroundColor={selected ? 'var(--global-cont-1)' : undefined}
    >
      <TableData
        borderBottom="1px solid var(--global-cont-2)"
        width="40px"
        onClick={(e) => e.stopPropagation()}
        padding="8px"
      >
        <Checkbox
          disabled={campaign.state === 'sent'}
          checked={selected}
          onChange={() => onSelect(campaign.uuid)}
          value={campaign.uuid}
          size={16}
        />
      </TableData>

      <TableData borderBottom="1px solid var(--global-cont-2)" width="200px">
        <div className="flex ai-center gap-8">
          <Icon name={ICONS.EMAIL_SEND} size={16} color="var(--global-ico-text-3)" />
          <Text className="P1" style={{ fontWeight: 600 }} color="var(--global-ico-text-1)" clamp={1}>
            {title}
          </Text>
        </div>
      </TableData>

      <TableData borderBottom="1px solid var(--global-cont-2)" width="120px">
        <div className="flex ai-center gap-8">
          <Icon name={ICONS.EMAIL_SEND} size={16} color="var(--global-ico-text-3)" />
          <Text className="P1" style={{ fontWeight: 600 }} color="var(--global-ico-text-1)" clamp={1}>
            {sourceScheduledEmail?.email_type ? EmailTypeDisplayNames[sourceScheduledEmail.email_type] : ''}
          </Text>
        </div>
      </TableData>

      <TableData borderBottom="1px solid var(--global-cont-2)" width="150px">
        <Text className="P2" color="var(--global-ico-text-3)">
          {scheduledAt ? niceDateTimeTz(scheduledAt) : '--'}
        </Text>
      </TableData>

      <TableData borderBottom="1px solid var(--global-cont-2)" width="150px">
        {includedAudiences && (
          <Text className="P2" color="var(--global-ico-text-3)">
            {includedAudiences.label}
            {includedAudiences.rest ? ` +${includedAudiences.rest}` : ''}
          </Text>
        )}
      </TableData>

      {/* <TableData borderBottom="1px solid var(--global-cont-2)" width="100px">
        <Text className="P2">{stats?.sent || 0}</Text>
      </TableData>

      <TableData borderBottom="1px solid var(--global-cont-2)" width="100px">
        <Text className="P2">{stats?.clickRate || 0}%</Text>
      </TableData> */}

      <TableData borderBottom="1px solid var(--global-cont-2)" width="120px">
        <Pill
          colorMix={{ color: statusColor.color, mix: statusColor.mix }}
          padding="4px 8px"
          borderRadius="16px"
          borderColor={statusColor.borderColor}
          borderWidth="1px"
        >
          <Text fontSize="12px" color={statusColor.color} style={{ fontWeight: 600 }}>
            {getCampaignStatusLabel(state)}
          </Text>
        </Pill>
      </TableData>
    </TableRow>
  );
};

export default CampaignsTableRow;
